import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";

import { ChevronDown, Search, CalendarDays, Car, Package } from "lucide-react";

import UserLayout from "../components/Userlayout";
import PartsDirectoryTest from "./PartsDirectoryTest";
import { MAKES_DATA, PARTS_DATA } from "./Data";
import heroBg from "../img/search.jpg";

const STORE_ID = "cmr7wh4ey030do35ydeft8ukq";

const API_KEY = "4560a5d74bfb3810ee113b21f210546d";

const BASE_URL = `https://api-sb.allgoodparts.com.au/api/storefront/stores/${STORE_ID}/parts`;
const MAIN_DOMAIN = "https://allgoodparts.com.au";

const FILTER_CHAIN = [

  { key: "year", param: "filter.p.m.custom.year", placeholder: "Select Year" },

  { key: "make", param: "filter.p.m.custom.make", placeholder: "Select Make" },

  { key: "model", param: "filter.p.m.custom.model", placeholder: "Select Model" },

  { key: "part", param: "filter.p.m.custom.part", placeholder: "Select Part" },

];

const MOCK_FACETS = {

  year: ["2024", "2023", "2022", "2021", "2020"],

  make: MAKES_DATA.map((m) => m.name),

  model: ["Model A", "Model B", "Model C"],

  part: PARTS_DATA.map((p) => p.name),

};

function normalizeValue(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/[^\w\s]/g, "");
}

function partToKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/a\/c/g, "a-c")
    .replace(/p\/s/g, "p-s")
    .replace(/c\/locking/g, "c-locking")
    .replace(/r\/bar/g, "r-bar")
    .replace(/f\/bar/g, "f-bar")
    .replace(/\./g, "")
    .replace(/[()]/g, "")
    .replace(/[\/&+]/g, "-")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

const MAKE_IMAGE_MAP = new Map(
  MAKES_DATA.map((item) => [normalizeValue(item.name), item.image])
);

const PART_IMAGE_MAP = new Map(
  PARTS_DATA.map((item) => [partToKey(item.name), item.image])
);

function getFilterImage(filterKey, value) {
  if (!value) return "";
  if (filterKey === "make") return MAKE_IMAGE_MAP.get(normalizeValue(value)) || "";
  if (filterKey === "part") return PART_IMAGE_MAP.get(partToKey(value)) || "";
  return "";
}

function FilterIcon({ filterKey, value, className = "h-6 w-6" }) {
  const image = getFilterImage(filterKey, value);

  if (image) {
    return (
      <img
        src={image}
        alt=""
        className={`${className} shrink-0 object-contain`}
        onError={(event) => {
          event.currentTarget.style.display = "none";
        }}
      />
    );
  }

  if (filterKey === "year") {
    return <CalendarDays className={`${className} shrink-0 text-gray-400`} />;
  }

  if (filterKey === "model") {
    return <Car className={`${className} shrink-0 text-gray-400`} />;
  }

  return <Package className={`${className} shrink-0 text-gray-400`} />;
}

function SearchableSelect({ filterKey, placeholder, options, value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);

  useEffect(() => {
    const close = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
        setQuery("");
      }
    };

    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const filtered = useMemo(
    () =>
      options.filter((option) =>
        String(option).toLowerCase().includes(query.toLowerCase())
      ),
    [options, query]
  );

  return (
    <div ref={rootRef} className="relative w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((current) => !current)}
        className={`flex h-12 w-full items-center gap-3 rounded-md bg-[#f8f8fa] px-4 text-left text-[13px]
          transition ${disabled ? "cursor-default opacity-50" : "cursor-pointer hover:bg-[#f4f4f7]"}
          ${value ? "font-medium text-[#30343c]" : "text-[#777b84]"}`}
      >
        <FilterIcon filterKey={filterKey} value={value} className="h-5 w-5" />
        <span className="min-w-0 flex-1 truncate">{value || placeholder}</span>
        <ChevronDown
          size={18}
          strokeWidth={2}
          className={`shrink-0 text-[#252a73] transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && !disabled && (
        <div className="absolute left-0 top-full z-[100] mt-2 w-full min-w-[220px] overflow-hidden rounded-md border border-[#e1e2e5] bg-white shadow-xl">
          <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2">
            <Search size={14} className="shrink-0 text-gray-400" />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search..."
              className="w-full bg-transparent text-[13px] text-gray-700 outline-none placeholder:text-gray-400"
            />
          </div>

          <div className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-[13px] text-gray-400">No matches</div>
            ) : (
              filtered.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => {
                    onChange(option);
                    setOpen(false);
                    setQuery("");
                  }}
                  className={`flex w-full items-center gap-3 px-3 py-2.5 text-left text-[13px] transition
                    hover:bg-[#fff1f2] ${option === value
                      ? "bg-[#fff4f4] font-semibold text-[#dd1921]"
                      : "text-[#343840]"
                    }`}
                >
                  <FilterIcon filterKey={filterKey} value={option} className="h-7 w-7" />
                  <span className="min-w-0 flex-1 truncate">{option}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function usePartsFinder() {

  const [selections, setSelections] = useState({});

  const [facets, setFacets] = useState({ year: [], make: [], model: [], part: [] });

  const [loading, setLoading] = useState(false);

  const fetchFacets = useCallback(async (params) => {

    try {

      const qs = new URLSearchParams(params).toString();

      const res = await fetch(qs ? `${BASE_URL}?${qs}` : BASE_URL, {

        headers: { "x-storefront-key": API_KEY },

      });

      if (!res.ok) throw new Error(`Filter API error: ${res.status}`);

      const data = await res.json();

      return data.facets || {};

    } catch {

      // Fallback so the UI stays usable without a live/reachable API.

      return {

        year: { selected: MOCK_FACETS.year },

        make: { selected: MOCK_FACETS.make },

        model: { selected: MOCK_FACETS.model },

        part: { selected: MOCK_FACETS.part },

      };

    }

  }, []);

  // initial load

  useEffect(() => {

    (async () => {

      setLoading(true);

      const data = await fetchFacets({});

      setFacets((prev) => ({ ...prev, year: data.year?.selected || [] }));

      setLoading(false);

    })();

  }, [fetchFacets]);

  const selectValue = useCallback(

    async (key, value) => {

      const order = FILTER_CHAIN.map((f) => f.key);

      const idx = order.indexOf(key);

      const nextSelections = { ...selections, [key]: value };

      // clear downstream selections

      order.slice(idx + 1).forEach((k) => delete nextSelections[k]);

      setSelections(nextSelections);

      // clear downstream facet options

      setFacets((prev) => {

        const next = { ...prev };

        order.slice(idx + 1).forEach((k) => (next[k] = []));

        return next;

      });

      const nextKey = order[idx + 1];

      if (!nextKey) return;

      setLoading(true);

      const data = await fetchFacets(nextSelections);

      setFacets((prev) => ({ ...prev, [nextKey]: data[nextKey]?.selected || [] }));

      setLoading(false);

    },

    [selections, fetchFacets]

  );

  const reset = useCallback(async () => {

    setSelections({});

    setLoading(true);

    const data = await fetchFacets({});

    setFacets({ year: data.year?.selected || [], make: [], model: [], part: [] });

    setLoading(false);

  }, [fetchFacets]);

  return { selections, facets, loading, selectValue, reset };

}

function PartsFinder({ onSearch }) {
  const { selections, facets, selectValue } = usePartsFinder();

  const handleSearch = () => {
    const params = {};

    FILTER_CHAIN.forEach(({ key, param }) => {
      if (selections[key]) params[param] = selections[key];
    });

    if (!Object.keys(params).length) return;
    onSearch?.(params);
  };

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-[repeat(4,minmax(0,1fr))_1.35fr] md:gap-2">
      {FILTER_CHAIN.map(({ key, placeholder }, index) => {
        const disabled =
          index > 0 && !selections[FILTER_CHAIN[index - 1].key];

        return (
          <SearchableSelect
            key={key}
            filterKey={key}
            placeholder={placeholder}
            options={facets[key] || []}
            value={selections[key] || ""}
            disabled={index > 0 && disabled}
            onChange={(value) => selectValue(key, value)}
          />
        );
      })}

      <button
        type="button"
        onClick={handleSearch}
        className="h-12 w-full rounded-md bg-[#0b7be7] px-5 text-[13px] font-medium text-white transition hover:bg-[#086fd1]"
      >
        Find My Part
      </button>
    </div>
  );
}

// function BrowseSection({ title, accent, items, type, onSelect, viewAllHref }) {
//   const visibleItems = items.slice(0, 6);

//   return (
//     <section className="rounded-lg bg-white px-5 py-5 md:px-6 md:py-5">
//       <div className="mb-7 flex items-center justify-between">
//         <h3 className="text-[18px] font-bold text-[#111]">
//           {title} <span className="text-[#f32835]">{accent}</span>
//         </h3>

//         <a
//           href={viewAllHref}
//           className="text-[12px] font-medium text-[#111] hover:text-[#f32835]"
//         >
//           View All
//         </a>
//       </div>

//       <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-6 md:gap-5">
//         {visibleItems.map((item) => (
//           <button
//             key={item.name}
//             type="button"
//             onClick={() => onSelect?.(item)}
//             className={`group flex min-w-0 flex-col items-center justify-center text-center ${type === "make"
//                 ? "min-h-[100px] rounded-md border border-[#f0f0f0] px-3 py-4 hover:border-[#e2e2e2]"
//                 : "px-2 py-1"
//               }`}
//           >
//             {type === "part" ? (
//               <span className="mb-3 flex h-[76px] w-[76px] items-center justify-center rounded-full border border-[#ffd9dc] bg-[#fff6f6]">
//                 <img
//                   src={item.image}
//                   alt=""
//                   className="h-12 w-12 object-contain transition-transform group-hover:scale-105"
//                 />
//               </span>
//             ) : (
//               <img
//                 src={item.image}
//                 alt=""
//                 className="mb-3 h-11 w-[78px] object-contain transition-transform group-hover:scale-105"
//               />
//             )}

//             <span className="line-clamp-2 text-[12px] font-bold capitalize text-[#111]">
//               {item.name}
//             </span>
//           </button>
//         ))}
//       </div>
//     </section>
//   );
// }

export default function FindPartsPage() {
  const handleSearch = (params) => {
    const query = new URLSearchParams(params).toString();
    window.location.href = `${MAIN_DOMAIN}/collections/all?${query}`;
  };

  const goToCollection = (param, item) => {
    window.location.href = `${MAIN_DOMAIN}/collections/all?${param}=${encodeURIComponent(
      item.name
    )}`;
  };

  return (
    <UserLayout>
      <main className="min-h-screen bg-[#f7f7f8] pb-8">
        <section
          className="relative min-h-[184px] overflow-hidden bg-[#050a12] bg-cover bg-center"
          style={{ backgroundImage: `url(${heroBg})` }}
        >
          <div className="agp-page-wrap flex min-h-[184px] items-center">
            <div>
              <h1 className="text-[36px] font-bold leading-[1.18] text-white">
                Find the
                <br />
                <span className="text-[#ff2938]">Right Part</span>
              </h1>
              <p className="mt-5 text-[14px] text-white">
                Select your vehicle details and we’ll do the rest
              </p>
            </div>
          </div>
        </section>

        <div className="agp-page-wrap pt-7">
          <section className="rounded-lg bg-white p-5 md:px-6 md:py-5">
            <PartsFinder onSearch={handleSearch} />
          </section>

          <div className="mt-4 space-y-2">
            {/* <BrowseSection
              title="Browse By"
              accent="Part"
              type="part"
              items={PARTS_DATA}
              viewAllHref={`${MAIN_DOMAIN}/collections/all`}
              onSelect={(item) =>
                goToCollection("filter.p.m.custom.part", item)
              }
            /> */}
            <PartsDirectoryTest />

            {/* <BrowseSection
              title="Browse By"
              accent="Make & Models"
              type="make"
              items={MAKES_DATA}
              viewAllHref={`${MAIN_DOMAIN}/collections/all`}
              onSelect={(item) =>
                goToCollection("filter.p.m.custom.make", item)
              }
            /> */}
          </div>
        </div>
      </main>
    </UserLayout>
  );
}
